import { AdminsListPage } from '@features/admins';
import { AgreementShowPage, AgreementsListPage } from '@features/agreements';
import { LoginPage, VerifyOtpPage } from '@features/auth';
import { DashboardPage } from '@features/dashboard';
import { DocumentsPage } from '@features/documents';
import { FeeTariffsPage } from '@features/fee-tariffs';
import { RbacPage } from '@features/rbac';
import { SettingsListPage } from '@features/settings';
import { UserShowPage, UsersListPage } from '@features/users';
import { accessControlProvider } from '@providers/access-control-provider';
import { authProvider } from '@providers/auth-provider';
import { dataProvider } from '@providers/data-provider';
import { i18nProvider } from '@providers/i18n-provider';
import { notificationProvider } from '@providers/notification-provider';
import { Refine } from '@refinedev/core';
import routerProvider from '@refinedev/react-router';
import { App as AntdApp, ConfigProvider } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { AdminLayout } from './app/AdminLayout';
import { Authenticated } from './app/Authenticated';
import { RequireAccess } from './app/RequireAccess';
import { lightTheme } from './app/theme';

/**
 * Корневой компонент. Собирает Refine с провайдерами, AntD ConfigProvider
 * с темой и роутер с приватными/публичными роутами.
 */
export function App() {
  const { t } = useTranslation('common');
  return (
    <ConfigProvider theme={lightTheme} locale={ruRU}>
      <AntdApp>
        <BrowserRouter>
          <Refine
            dataProvider={dataProvider}
            authProvider={authProvider}
            accessControlProvider={accessControlProvider}
            i18nProvider={i18nProvider}
            notificationProvider={notificationProvider}
            routerProvider={routerProvider}
            resources={[
              { name: 'settings', list: '/settings', meta: { label: t('menu.settings') } },
              { name: 'documents', list: '/documents', meta: { label: t('menu.documents') } },
              { name: 'users', list: '/users', meta: { label: t('menu.users') } },
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              disableTelemetry: true,
            }}
          >
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/verify-otp" element={<VerifyOtpPage />} />

              <Route
                element={
                  <Authenticated>
                    <AdminLayout />
                  </Authenticated>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route
                  path="/settings"
                  element={
                    <RequireAccess resource="settings" action="list">
                      <SettingsListPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/agreements"
                  element={
                    <RequireAccess resource="agreements" action="list">
                      <AgreementsListPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/agreements/:id"
                  element={
                    <RequireAccess resource="agreements" action="show">
                      <AgreementShowPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/fee-tariffs"
                  element={
                    <RequireAccess resource="fee-tariffs" action="list">
                      <FeeTariffsPage />
                    </RequireAccess>
                  }
                />
                <Route path="/documents" element={<DocumentsPage />} />
                <Route
                  path="/users"
                  element={
                    <RequireAccess resource="users" action="list">
                      <UsersListPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/users/:id"
                  element={
                    <RequireAccess resource="users" action="show">
                      <UserShowPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/admins"
                  element={
                    <RequireAccess resource="admins" action="list">
                      <AdminsListPage />
                    </RequireAccess>
                  }
                />
                <Route
                  path="/rbac"
                  element={
                    <RequireAccess resource="roles" action="list">
                      <RbacPage />
                    </RequireAccess>
                  }
                />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Refine>
        </BrowserRouter>
      </AntdApp>
    </ConfigProvider>
  );
}
